import type { Store } from '@reduxjs/toolkit';
import getOr from 'lodash/fp/getOr';
import { trace } from '../../configuration/setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale, supportedLocaleMap } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

// Webpack is weird sometimes, maybe it's Babel, who knows...
const normalizeDynamicImport = (imported: unknown) => getOr(imported, 'default', imported);

const sanitizeLocale = (locale: string): string => {
    const sanitized = locale.replaceAll(/[^a-zA-Z-]/g, '');
    return Object.hasOwn(supportedLocaleMap, sanitized) ? sanitized : DEFAULT_LOCALE;
};

export const importDisplayMessages = (locale: string) => {
    const safeLocale = sanitizeLocale(locale);
    return import(`../../configuration/translations/${safeLocale}.json`).then(normalizeDynamicImport).catch(error => {
        trace.error(`Could not "../../configuration/translations/${safeLocale}.json" file.`, error);
        return error;
    });
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            return Promise.reject(new Error('No "locale" supplied when fetching display messages'));
        }

        store.dispatch(localeChanged(locale));
        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error: unknown) {
            trace.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
